import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { BookOpen, Plus, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { JournalEntryRow } from "@/components/journal/JournalEntryRow";
import { useActiveCompany } from "@/lib/company-context";
import { useJournalEntries } from "@/lib/api/journal.queries";

export const Route = createFileRoute("/companies/$companyId/contabilidad/diario/")({
  component: DiarioPage,
});

const DOC_TYPES = [
  "all",
  "sales-invoice",
  "sales-credit-note",
  "sales-debit-note",
  "purchase-support-document",
  "payroll-payslip",
  "manual",
] as const;

const STATUSES = ["all", "posted", "reversed", "voided"] as const;

function DiarioPage() {
  return (
    <RequireAuth>
      <AppShell>
        <DiarioContent />
      </AppShell>
    </RequireAuth>
  );
}

function DiarioContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [q, setQ] = useState("");
  const [docType, setDocType] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useJournalEntries(companyId, {
    q: q || undefined,
    documentType: docType !== "all" ? docType : undefined,
    status: status !== "all" ? status : undefined,
    periodFrom: periodFrom || undefined,
    periodTo: periodTo || undefined,
    page,
    pageSize: 50,
  });

  const totalPages = data ? Math.ceil(data.total / 50) : 1;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <BookOpen className="h-5 w-5 text-muted-foreground" />
            {t("nav.contabilidad.diario")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("journal.diario.subtitle")}
          </p>
        </div>
        <Link
          to="/companies/$companyId/contabilidad/diario/nuevo"
          params={{ companyId }}
        >
          <Button size="sm">
            <Plus className="mr-1.5 h-4 w-4" />
            {t("journal.actions.newManualEntry")}
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder={t("journal.diario.searchPlaceholder")}
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
          />
        </div>
        <select
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={docType}
          onChange={(e) => { setDocType(e.target.value); setPage(1); }}
        >
          {DOC_TYPES.map((dt) => (
            <option key={dt} value={dt}>
              {dt === "all"
                ? t("journal.diario.allDocTypes")
                : t(`journal.docType.${dt}`, { defaultValue: dt })}
            </option>
          ))}
        </select>
        <select
          className="h-9 rounded-md border bg-background px-3 text-sm"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s === "all"
                ? t("journal.diario.allStatuses")
                : t(`journal.status.${s}`, { defaultValue: s })}
            </option>
          ))}
        </select>
        <Input
          type="date"
          className="h-9 w-36"
          value={periodFrom}
          onChange={(e) => { setPeriodFrom(e.target.value); setPage(1); }}
          placeholder="Desde"
        />
        <Input
          type="date"
          className="h-9 w-36"
          value={periodTo}
          onChange={(e) => { setPeriodTo(e.target.value); setPage(1); }}
          placeholder="Hasta"
        />
      </div>

      {/* Table */}
      {isError ? (
        <ErrorState title={t("journal.error.loadEntries")} />
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : !data?.entries.length ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          {t("journal.diario.empty")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left text-xs font-medium text-muted-foreground">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">{t("journal.entry.date")}</th>
                <th className="px-4 py-3">{t("journal.entry.description")}</th>
                <th className="px-4 py-3">{t("journal.entry.type")}</th>
                <th className="px-4 py-3 text-right">{t("journal.line.debit")}</th>
                <th className="px-4 py-3 text-right">{t("journal.line.credit")}</th>
                <th className="px-4 py-3">{t("journal.entry.status")}</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((entry) => (
                <JournalEntryRow
                  key={entry.id}
                  entry={entry}
                  companyId={companyId}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {(data?.total ?? 0) > 50 ? (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {t("common.pagination.showing", {
              from: Math.min((page - 1) * 50 + 1, data?.total ?? 0),
              to: Math.min(page * 50, data?.total ?? 0),
              total: data?.total ?? 0,
            })}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t("common.pagination.prev")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("common.pagination.next")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
